import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const appStoreUrl = "https://apps.apple.com/jp/app/id1169500173";
const googlePlayUrl = "https://play.google.com/store/apps/details?id=jp.s2mr.shiftas";

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5fbf7;
  color: #143124;
`;

const Header = styled.header`
  position: relative;
  z-index: 1;
  padding: 1.25rem 1.5rem 0;

  @media (max-width: 768px) {
    padding: 1rem 1rem 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Logo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #12351f;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const HeaderIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 117, 72, 0.16);
`;

const LogoText = styled.h1`
  margin: 0;
  font-size: 1.375rem;
  line-height: 1;
`;

const LoginLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 1rem;
  border-radius: 4px;
  background: #00875a;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 8px 18px rgba(0, 117, 72, 0.16);

  &:hover {
    background: #006644;
  }
`;

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: 4rem 1.5rem 5rem;

  &::before {
    content: "";
    position: absolute;
    inset: -12rem -10rem auto auto;
    width: 34rem;
    height: 34rem;
    border-radius: 999px;
    background: rgba(97, 211, 137, 0.2);
  }

  &::after {
    content: "";
    position: absolute;
    inset: auto auto -16rem -12rem;
    width: 32rem;
    height: 32rem;
    border-radius: 999px;
    background: rgba(0, 135, 90, 0.1);
  }

  @media (max-width: 768px) {
    padding: 2.75rem 1rem 3.5rem;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 3.5rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
  }
`;

const HeroText = styled.div`
  max-width: 640px;

  @media (max-width: 900px) {
    max-width: 720px;
    margin: 0 auto;
  }
`;

const ReleaseBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid rgba(0, 102, 68, 0.14);
  border-radius: 999px;
  background: rgba(223, 247, 233, 0.9);
  color: #006644;
  font-size: 0.875rem;
  font-weight: 800;
`;

const BadgeDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #36b37e;
  box-shadow: 0 0 0 5px rgba(54, 179, 126, 0.16);
`;

const HeroTitle = styled.h2`
  margin: 0;
  color: #12351f;
  font-size: clamp(2.5rem, 6vw, 4.8rem);
  font-weight: 900;
  line-height: 1.02;
  letter-spacing: -0.06em;
`;

const HeroSubtitle = styled.p`
  max-width: 34rem;
  margin: 1.5rem 0 0;
  color: #416653;
  font-size: 1.125rem;
  line-height: 1.8;

  @media (max-width: 900px) {
    margin-right: auto;
    margin-left: auto;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const StoreButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-top: 2rem;

  @media (max-width: 900px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const StoreButton = styled.a`
  display: inline-flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  border-radius: 999px;
  padding: 0.875rem 1.35rem;
  background: #ffffff;
  color: #12351f;
  box-shadow: 0 14px 30px rgba(0, 117, 72, 0.1), inset 0 0 0 1px rgba(0, 102, 68, 0.12);
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.3;
  text-decoration: none;
  transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 36px rgba(0, 117, 72, 0.14), inset 0 0 0 1px rgba(0, 102, 68, 0.14);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 3px solid rgba(54, 179, 126, 0.36);
    outline-offset: 3px;
  }
`;

const ButtonMark = styled.span`
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(54, 179, 126, 0.16);
  color: inherit;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const HeroVisual = styled.div`
  justify-self: center;
  width: min(100%, 360px);
`;

const IconShowcase = styled.div`
  position: relative;
  min-height: 360px;
  border-radius: 40px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.96), rgba(225, 249, 235, 0.94));
  box-shadow: 0 28px 80px rgba(0, 117, 72, 0.16);
  padding: 2rem;
  overflow: hidden;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 999px;
    background: rgba(54, 179, 126, 0.16);
  }

  &::before {
    width: 12rem;
    height: 12rem;
    top: -4rem;
    right: -4rem;
  }

  &::after {
    width: 8rem;
    height: 8rem;
    bottom: -3rem;
    left: -2rem;
  }

  @media (max-width: 480px) {
    min-height: 300px;
    border-radius: 32px;
  }
`;

const AppIcon = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: 148px;
  height: 148px;
  margin: 1rem auto 1.5rem;
  border-radius: 34px;
  box-shadow: 0 24px 48px rgba(0, 117, 72, 0.28);
`;

const VisualCard = styled.div`
  position: relative;
  z-index: 1;
  border: 1px solid rgba(0, 102, 68, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.78);
  padding: 1rem;
  backdrop-filter: blur(14px);
`;

const VisualLabel = styled.p`
  margin: 0;
  color: #006644;
  font-size: 0.8125rem;
  font-weight: 800;
`;

const VisualText = styled.p`
  margin: 0.5rem 0 0;
  color: #416653;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const MainContent = styled.main`
  background: #ffffff;
`;

const Section = styled.section`
  padding: 4.75rem 1.5rem;

  @media (max-width: 768px) {
    padding: 3.5rem 1rem;
  }
`;

const SectionInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  max-width: 640px;
  margin: 0 auto 2rem;
  text-align: center;
`;

const SectionEyebrow = styled.p`
  margin: 0 0 0.75rem;
  color: #00875a;
  font-size: 0.8125rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #12351f;
  font-size: clamp(1.875rem, 4vw, 2.75rem);
  font-weight: 900;
  line-height: 1.12;
  letter-spacing: -0.04em;
`;

const SectionDescription = styled.p`
  margin: 1rem 0 0;
  color: #5b7566;
  font-size: 1rem;
  line-height: 1.8;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  border: 1px solid #e2efe7;
  border-radius: 28px;
  background: #fbfffc;
  padding: 1.5rem;
  box-shadow: 0 18px 50px rgba(0, 79, 46, 0.06);
`;

const FeatureNumber = styled.div`
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #dff7e9;
  color: #006644;
  font-size: 0.875rem;
  font-weight: 900;
  margin-bottom: 1.25rem;
`;

const FeatureTitle = styled.h3`
  margin: 0;
  color: #12351f;
  font-size: 1.125rem;
  font-weight: 900;
  line-height: 1.4;
`;

const FeatureDescription = styled.p`
  margin: 0.75rem 0 0;
  color: #5b7566;
  font-size: 0.9375rem;
  line-height: 1.75;
`;

const AppSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f5fbf7 100%);
`;

const InfoPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 2rem;
  align-items: start;
  border: 1px solid #dbeee3;
  border-radius: 36px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 22px 70px rgba(0, 79, 46, 0.08);
  padding: 2rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: 1.25rem;
    border-radius: 28px;
  }
`;

const AppSummary = styled.div`
  border-radius: 28px;
  background: #12351f;
  color: #ffffff;
  padding: 2rem;

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const AppTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.04em;
`;

const AppDescription = styled.p`
  margin: 1rem 0 0;
  color: #d8f7e6;
  font-size: 0.98rem;
  line-height: 1.8;
`;

const BadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  margin-top: 1.5rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  padding: 0.45rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 800;
`;

const AppFeatureList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0;
  padding: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AppFeatureItem = styled.li`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  border: 1px solid #e2efe7;
  border-radius: 18px;
  background: #ffffff;
  padding: 0.875rem;
  color: #244c36;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.55;

  &::before {
    content: "";
    display: block;
    width: 0.55rem;
    height: 0.55rem;
    flex: 0 0 auto;
    margin-top: 0.45rem;
    border-radius: 999px;
    background: #36b37e;
    box-shadow: 0 0 0 5px rgba(54, 179, 126, 0.12);
  }
`;

const Footer = styled.footer`
  background: #f5fbf7;
  padding: 2rem 1.5rem;
  text-align: center;
`;

const FooterText = styled.p`
  margin: 0;
  color: #6c8274;
  font-size: 0.875rem;
`;

export const HomePage: React.FC = () => {
  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Logo aria-label="Shiftas">
            <HeaderIcon src="/app-icon.jpg" alt="" aria-hidden="true" />
            <LogoText>Shiftas</LogoText>
          </Logo>
          <LoginLink to="/login">ログイン</LoginLink>
        </HeaderContent>
      </Header>

      <HeroSection>
        <HeroContent>
          <HeroText>
            <ReleaseBadge>
              <BadgeDot />
              Android版を公開しました
            </ReleaseBadge>
            <HeroTitle>みんなのシフトをスマホでかんたん管理</HeroTitle>
            <HeroSubtitle>
              複数人のシフト入力・共有に特化したシフト管理アプリです。iOSとAndroidで入力したシフトを、Webでも見やすく確認できます。
            </HeroSubtitle>
            <StoreButtons>
              <StoreButton href={appStoreUrl} target="_blank" rel="noopener noreferrer">
                <ButtonMark>iOS</ButtonMark>
                App Storeからダウンロード
              </StoreButton>
              <StoreButton href={googlePlayUrl} target="_blank" rel="noopener noreferrer">
                <ButtonMark>GP</ButtonMark>
                Google Playで入手
              </StoreButton>
            </StoreButtons>
          </HeroText>

          <HeroVisual aria-hidden="true">
            <IconShowcase>
              <AppIcon src="/app-icon.jpg" alt="" />
              <VisualCard>
                <VisualLabel>iOS / Android 対応</VisualLabel>
                <VisualText>シフト入力はスマホで。共有されたシフト表はWebからも確認できます。</VisualText>
              </VisualCard>
            </IconShowcase>
          </HeroVisual>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <Section>
          <SectionInner>
            <SectionHeader>
              <SectionEyebrow>Web Preview</SectionEyebrow>
              <SectionTitle>共有されたシフトを、見やすく確認</SectionTitle>
              <SectionDescription>
                Webアプリは閲覧に特化。勤務先メンバーの予定を一覧で把握しやすいように、必要な情報をシンプルに整理します。
              </SectionDescription>
            </SectionHeader>
            <FeatureGrid>
              <FeatureCard>
                <FeatureNumber>01</FeatureNumber>
                <FeatureTitle>直感的な確認</FeatureTitle>
                <FeatureDescription>余計な操作を減らし、共有されたシフトをすばやく確認できます。</FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureNumber>02</FeatureNumber>
                <FeatureTitle>見やすい表示</FeatureTitle>
                <FeatureDescription>色分けされたシフト表で、誰がいつ働くのかを一目で把握できます。</FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureNumber>03</FeatureNumber>
                <FeatureTitle>チームで共有</FeatureTitle>
                <FeatureDescription>スマホアプリで更新した内容を共有し、チーム全体で最新の予定を確認できます。</FeatureDescription>
              </FeatureCard>
            </FeatureGrid>
          </SectionInner>
        </Section>

        <AppSection>
          <SectionInner>
            <InfoPanel>
              <AppSummary>
                <AppTitle>シフト入力・管理はシフタスで</AppTitle>
                <AppDescription>
                  シフトの入力・編集はスマホアプリで行います。スマートな入力画面で、複数人のシフト管理をもっと軽くできます。
                </AppDescription>
                <BadgeGroup>
                  <Badge>評価 4.2</Badge>
                  <Badge>iOS対応</Badge>
                  <Badge>Android対応</Badge>
                  <Badge>1,200+人が利用</Badge>
                </BadgeGroup>
              </AppSummary>

              <AppFeatureList>
                <AppFeatureItem>オンラインでのシフト共有機能</AppFeatureItem>
                <AppFeatureItem>複数の勤務先管理機能</AppFeatureItem>
                <AppFeatureItem>シフトの自動入力と色分け</AppFeatureItem>
                <AppFeatureItem>変則的な予定の入力対応</AppFeatureItem>
                <AppFeatureItem>PDFとJPEGでのシフト書き出し</AppFeatureItem>
                <AppFeatureItem>従業員ごとの勤務状況確認</AppFeatureItem>
                <AppFeatureItem>その日に働く人一覧表示</AppFeatureItem>
                <AppFeatureItem>複数端末からのログイン対応</AppFeatureItem>
              </AppFeatureList>
            </InfoPanel>
          </SectionInner>
        </AppSection>
      </MainContent>

      <Footer>
        <FooterText>© 2025 Shiftas. All rights reserved.</FooterText>
      </Footer>
    </PageContainer>
  );
};
